/*jshint esversion: 6 */
/*! webmd.useragent */

/**
 * Sniffs out user agent string.
 *
 * <p>Adds classes to HTML element for certain browsers (xx = major version, yy = minor version)</p>
 *
 * <dl>
 *   <dt>Firefox:</dt>
 *   <dd>.browserFF .browserFF_xx browserFF_xx_yy</dd>
 *
 *   <dt>Internet Explorer:</dt>
 *   <dd>.browserIE .browserIE_xx browserIE_xx_yy</dd>
 *
 *   <dt>WebKit:</dt>
 *   <dd>.browserWK .browserWK_xx browserWK_xx_yy</dd>
 * </dl>
 *
 * <p>Also adds two CSS classes to the document so one can create overrides for specific types or devices</p>
 *
 * <pre>
 * .mydiv { width:400px; }
 * .ua_type_mobile .mydiv { width:100%; }
 * .ua_type_iphone .mydiv { width:320px; }
 * </pre>
 *
 * @namespace
 */

// dependencies
import { customEvent } from 'webmd-core';
import debug from 'webmd-debug';

export let getDevice;
export let getDeviceVer;
export let getType;
export let getTouch;
export let setupResponsive;
export let whichWidth;
export let getWidth;
export let isThin;
export let agent;
export let ua;

if (typeof window !== 'undefined') {

  let _getNavigatorUserAgent;
  let _singleResizeEvent;
  let _triggerResponsiveEvents;
  let _setResponsiveHTMLClasses;

  //polyfil the CustomEvent object in IE
  customEvent();

  ua = {
    type:'desktop', device:'pc', browser:'', major_version:'', minor_version:''
  };

  let oldWidth;

  let init = function() {
    let matches = '';
    let html = document.querySelector("html");

    agent = _getNavigatorUserAgent();

    /* Test for MSIE x.x; */
    if (matches == /MSIE\s(\d+)\.(\d+);/.exec(agent)) {
      ua.browser = 'browserIE';
      ua.major_version = matches[1];
      ua.minor_version = matches[2];
    /* Test for Firefox/x.x or Firefox x.x */
    } else if (matches == /Firefox[\/\s](\d+)\.(\d+)/.exec(agent)){
      ua.browser = 'browserFF';
      ua.major_version = matches[1];
      ua.minor_version = matches[2];
    /* Test for Google Chrome, regardless of mobile, tablet, or desktop x.x; */
    } else if (matches == /Chrome\/(\d+)\.(\d+)/.exec(agent)) {
      ua.browser = 'browserCH';
      ua.major_version = matches[1];
      ua.minor_version = matches[2];
    /* Test for Branded Safari x.x; */
    } else if (agent.indexOf('Safari') > -1 && agent.indexOf('Chrome') === -1) {
      // grabs the major and minor version of Safari
      matches = /like Gecko\) \w*\/(\d+)\.(\d+)/.exec(agent);
      ua.browser = 'browserSF';
      ua.major_version = matches[1];
      ua.minor_version = matches[2];
    /* Test for Generic WebKit if it fails all the branded tests x.x; */
    } else if (matches == /AppleWebKit\/(\d+)\.(\d+)/.exec(agent)) {
      ua.browser = 'browserWK';
      ua.major_version = matches[1];
      ua.minor_version = matches[2];
    }

    // if ((agent.indexOf('AppleWebKit') !== -1) && ((agent.indexOf('Mobile') !== -1) || (agent.indexOf('webOS') !== -1)) ) {
    //   ua.type = 'mobile';
    // }
    if ((agent.indexOf('iPad') !== -1)) {
      // ua.type = 'tablet';
      ua.device = 'ipad';
    }
    if ((agent.indexOf('iPhone') !== -1)) {
      // ua.type = 'mobile';
      ua.device = 'iphone';
    }
    if ((agent.indexOf('Android') !== -1)) {
      // check for Android tablets
      // if (agent.indexOf('Mobile') === -1) {
      //   ua.type = 'tablet';
      // } else {
      //   ua.type = 'mobile';
      // }
      ua.deviceVer = parseInt(agent.match(/Android [0-9.]+/)[0].replace('Android ',''));
      ua.device = 'android';
    }
    if ((agent.indexOf('Blackberry') !== -1)) {
      // ua.type = 'mobile';
      ua.device = 'blackberry';
    }
    if ((agent.indexOf('Silk') !== -1) || (agent.indexOf('Kindle') !== -1)) {
      // ua.type = 'tablet';
      if ((agent.indexOf('Android') === -1)) {
        ua.device = 'kindle';
      }
    }

    //for viewport size between 768px and 1025px
    if(getWidth() == 'thin'){
      ua.type = 'tablet';
    }
    //for viewport size less than 768px
    if(getWidth() == 'small'){
      ua.type = 'mobile';
    }
    //for viewport size greather than 1025px
    if(getWidth() == 'normal'){
      ua.type = 'desktop';
    }

    
    if ((agent.indexOf('webmdapp') !== -1)) {
      ua.appview = 'webmdapp';
    }

    const classesToAdd = [
    'ua_type_' + ua.type,
    'ua_device_' + ua.device
    ];
 
    if (ua.deviceVer) {
      classesToAdd.push('ua_deviceVer_' + ua.deviceVer);
    }
  
    if (ua.appview) {
      classesToAdd.push('ua_app_' + ua.appview);
    }
  
    if (ua.browser) {
      classesToAdd.push(ua.browser);
      classesToAdd.push(ua.browser + '_' + ua.major_version);
      classesToAdd.push(ua.browser + '_' + ua.major_version + '_' + ua.minor_version);
  
      if (ua.browser === 'browserIE' && document.documentMode) {
        let doc = document.documentMode;
        classesToAdd.push('IEDocMode_' + (doc === 5 ? 'quirks' : doc));
      }
    }
  
    html.classList.remove('no-js');
    classesToAdd.push('js'); 
    html.classList.add(...classesToAdd);
  };

  /**
   * Private function that just grabs and returns the navigator.useragent. Using this to write unit testing.
   *
   * @return {string} navigator.userAgent
   */
  _getNavigatorUserAgent = function() {  
    return navigator.userAgent;
  };

  /**
   * Retrieves the device.
   *
   * "pc" - Normal computer
   * "ipad" - Has iPad in the user agent string
   * "iphone" - Has iPhone in the user agent string
   * "android" - Has Android  in the user agent
   *
   * @return {string} Device
   */
  getDevice = function() {
    return ua.device;
  };

  getDeviceVer = function() {
    return ua.deviceVer;
  };

  /**
   * Retrieves the device type.
   *
   * "desktop" - The default value for a regular computer
   * "tablet" - Currently defining this as ipad only. Will include android tablets
   * "mobile" - The WebMD definition of mobile which is a webkit based mobile browser
   *
   * @return {string} Device type
   */
  getType = function() {
    return ua.type;
  };

  /**
   * Checks if this is a touch device.
   *
   * true - ontouchstart is a valid function and it meets our standards for a "touch" device
   * false - doesn't contain said touch function
   *
   * @return {Boolean}
   */
  getTouch = function() {
    debug('webmd.useragent.getTouch is deprecated, please use modernizr instead');
    return false;
  };

  /** The following are responsive functions to be used for the flexible template. 
   *  Putting them in here as it's MUCH easier just having one public API and letting pages call these functions or not. 
   * The Flexible base template will include JS that will call the "setupResponsive" function
   */


  /**
   * setupResponsive runs the function to set classes and attaches to the resize event to add or remove the thin class if you resize your browser during a page view
   *
   * Also does a bit of hackey user agent sniffing to make sure that our most popular tablets look awesome. They all have their own way of interpreting desktop 
   * web pages, and this baselines them. 
   * 
   * @public
   */
  setupResponsive = function() {
    let head = document.querySelector("head");
    let meta = document.createElement('meta');

    /* Adding a meta tag so that iPad plays nice and pretty with the thinner version of our site */
    if(navigator.userAgent.indexOf("iPad")!==-1){
      meta.name = "viewport";
      meta.content = "width=device-width";
      head.append(meta);
    } else if (getType() === 'mobile') {
      meta.name = "viewport";
      meta.content = "width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no";
      head.appendChild(meta);
    }

    // calling responsive classes initially
    _setResponsiveHTMLClasses();

    // sets the "old width". This is used when we compare the current width on window resize to see if we should trigger our custom resize event
    oldWidth = whichWidth();

    // setting up a single timed out resize event to reset the responsive classes and also call our custom event
    _singleResizeEvent( () => {
      _setResponsiveHTMLClasses();
      _triggerResponsiveEvents();
    });
  };

  /**
   * This is our custom resize event handler. It uses a 250ms timeout to make sure we don't constantly fire the resize event. 
   * @private
   */
  _singleResizeEvent = function(callback,timeout) {

    window.addEventListener('resize', function(){
      clearTimeout(timeout);
      timeout = setTimeout(callback,250);
    });

    return callback;
  };

  /**
   * This creates a simple window based custom event for a responsiveResize. The 'responsiveResize' event carries a custom parameter which is the new
   * size of the window. This custom event will only get called when the window changes types not just on the resize itself
   *
   * @example
   * window.addEventListener('responsiveResize',function(e){console.log(e.detail);});
   * 
   * @private
   */
  _triggerResponsiveEvents = function() {

    if (oldWidth === whichWidth()) {
      return;
    }
    else {
      window.dispatchEvent(new CustomEvent('responsiveResize', {detail: whichWidth()}));
      oldWidth = whichWidth();
    }
  };

  /**
   * Sets HTML classes depending on screen width 
   *
   * @private
   */
  _setResponsiveHTMLClasses = function() {
    let html = document.querySelector("html"); 

    // using global call here as we're calling this function inside of a setTimeout
    ['small', 'thin', 'normal'].forEach( (size) => {
      html.classList.remove(size);
    });

    html.classList.add(whichWidth());    
  };

  /**
   * DEPRACATED - use getWidth() instead
   *
   * @depracated
   */
  whichWidth = function() {
    return getWidth();
  };

  /**
   * Public function which returns a string with 'thin' or 'normal' depending on which size of site we have
   *
   * @returns {String} the name of the site size
   *
   * @example
   * if (webmd.useragent.getWidth() = 'thin') { doSomething(); }
   *
   * @public
   */
  getWidth = function() {

    /* If we can run with media queries, use those, it's nice and clean. Otherwise, let's borrow jQuery.
     *
     * Sometimes the window width doesn't get reported to us right, so just in case if you are on a 1024 screen, 
     * we'll give you the thin version anyways, as it would look ugly having horizontal scroll.
     * 
     * We are using ems instead of hardcoded pixels here (px / 16) in order to provide a good experience to zoomed
     * in users on bigger browser screens. 
     *
     * The general idea here is: 
     * Desktops and Laptops will get a 968px "normal" view and if they make their window smaller they will
     * get a thin view. If they go too small, we'll go with horizonal scroll vs a small view. The "small" view would trigger an
     * ad refresh to a 300x50 ad vs the 728x90 so we want to avoid doing that. 
     * 
     * Tablets in Portrait get a 768px "thin" view at 100% zoom. In Landscape they still get the 768px "thin" view, but we let the
     * device zoom in vs trying to fill out the screen. This gives people bigger font size and a 1.3 zoom for a better experience
     *
     * Smaller devices (phones and phablets) get the "small" view, which is a 100% width view for devices smaller than 768 physical
     * or simulated pixels but with 1 rail instead of 2. If you have a phablet landscape, you'll get some extra content/whitespace, but if you 
     * have an iphone or somewhere in the range of 320-400px physical pixels, you'll get the best optimized view.
     * 
     */
    if (window.matchMedia) {
      // Leaving this in here, but commenting it out. Right now, we're going to push big screens with tiny browsers into the thin
      // view as we don't want to have to switch out ads
      // if (window.matchMedia("(max-width: 788px) and (min-device-width: 1025px)").matches) {
      //  // matches desktops or otherwise big screens where someone either smooshed the display or isn't viewing something full size
      //  return 'small';
      // } else 
      if (window.matchMedia("(max-width: 50em) and (max-device-width: 767px)").matches) {
        // matches phones, phablets and other small screen devices that are smaller than 788px (accounts for desktop browsers with scrollbar). Has an out for screen size, so that ipad and other 768px tablets do not get this view. 
        return 'small';
      } else if (window.matchMedia("(max-width: 63em), (max-device-width: 1025px)").matches) {
        // This is our tablet view. If you are under 1000, we'll go thin width. That's 980 + scrollbars. This view is 768px to match the majority of tablets. We don't hardcode a zoom, so smaller or bigger tablets will zoom in on this width. Landscape tablets will also zoom to this px view providing a nice 1.3 zoom ratio with better usability
        return 'thin';
      }
      else {
        return 'normal';
      }
    }
    else {
      // this is our exception case for older browsers. If you have a smaller window and a crappy browser, we'll give you a thin view, but that's it. 
      if (window.innerWidth < 1000) {  
        return 'thin';
      }
      else { 
        return 'normal';
      }
    }
  };

  /**
   * Public function which returns a boolean if we have a thin site
   *
   * @returns {Boolean} 
   *
   * @example
   * if (!webmd.useragent.isThin()) { doSomething(); }
   *
   * @public
   */
  isThin = function() {
    return whichWidth() === 'thin';
  }

  /* Initilizing now so this can be put on the document before HTML starts to load */
  init();
}
